import { useCallback, useEffect, useRef, useState } from "react";
import { LensPlatformClient, LensSDKException, UserBusinessRole } from "lens-platform-sdk";
import { useLensPlatformClient } from "src/hooks/useLensPlatformClient";
import { useAnalytics } from "src/hooks/useAnalytics";

type ActionResponse = Awaited<ReturnType<LensPlatformClient["business"]["changeBusinessUserRole"]>>;

export const useChangeBusinessUserRole = () => {
  const lensPlatformClient = useLensPlatformClient();
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<ActionResponse>();
  const [error, setError] = useState<LensSDKException>();
  const { track, trackError } = useAnalytics();

  // Keep up with mounted/unmounted state
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const changeBusinessUserRole = useCallback(
    async (businessId: string, businessUserId: string, role: UserBusinessRole) => {
      let lensSDKException: LensSDKException | undefined;

      try {
        if (mounted.current) {
          setError(undefined);
          setLoading(true);
        }
        const response = await lensPlatformClient.business.changeBusinessUserRole(businessId, businessUserId, role);

        if (mounted.current) {
          track("Change business user role");
          setResponse(response);
        }
      } catch (exception) {
        if (exception instanceof LensSDKException) {
          trackError("Change business user failed");
          setError(exception);
          lensSDKException = exception;
        }
      } finally {
        if (mounted.current) {
          setLoading(false);
        }
      }

      return lensSDKException;
    },
    [lensPlatformClient.business, track, trackError],
  );

  return {
    changeBusinessUserRole,
    error,
    loading,
    response,
  };
};
